/**
 * Environment-aware audience gate for the clinical content Library surface.
 *
 * Production-only audience narrowing for the Library surface (the `libraryCms`
 * env kill-switch was retired at GA): outside production every signed-in user
 * is in the audience (staging is open to everyone); in production the surface
 * is reachable only by ICT professionals (HL7 `ict`) and clinical directors.
 * It mirrors `useBandwidthDiagnosticsAccess`, but the role gate is scoped to
 * production instead of applying in every environment.
 *
 * @agentic connections
 */

import type { AppEnv } from "@benchmark/core/feature-flags";
import type { PractitionerRoleBusinessCode } from "@benchmark/core/constants";
import { useCurrentUser } from "@/app/auth/CurrentUserContext";

/**
 * Business roles allowed to reach the Library surface in production. ICT
 * professionals are covered separately via `isIctProfessional` because `ict`
 * is the standard HL7 practitioner-role code, not a Benchmark business code.
 * @agentic connections
 */
export const LIBRARY_PRODUCTION_ROLES: PractitionerRoleBusinessCode[] = [
  "clinical-director",
];

/** Result of {@link useLibraryAudience}. @agentic connections */
export type LibraryAudience = {
  /** True when the current user is within the Library audience for this environment. */
  allowed: boolean;
  /** True while the production role gate is active and roles are still resolving. */
  pending: boolean;
};

/**
 * Resolves whether the current user is in the Library audience for the current
 * environment. Read `import.meta.env.MODE` inside the hook (not at module load)
 * so the environment can be stubbed in tests.
 * @agentic connections
 */
export const useLibraryAudience = (): LibraryAudience => {
  const { isIctProfessional, hasAnyRole, isLoading, isRolesResolved } =
    useCurrentUser();

  const isProduction = (import.meta.env.MODE as AppEnv) === "production";
  if (!isProduction) {
    return { allowed: true, pending: false };
  }

  const pending = isLoading || !isRolesResolved;
  const allowed =
    !pending && (isIctProfessional || hasAnyRole(LIBRARY_PRODUCTION_ROLES));

  return { allowed, pending };
};
